import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiDollarSign, FiPieChart, FiTrendingUp, FiLayers } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import useReportData from "../../useReportData";

const ProcurementSpendAnalysisReport = () => {
  const gridRef = useRef(null);
  const [data] = useReportData("ProcurementSpend");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalSpend = data.reduce((s, r) => s + (r.totalPurchaseValue || 0), 0);
    const categoryTotals = {};
    data.forEach((r) => {
      categoryTotals[r.materialCategory] =
        (categoryTotals[r.materialCategory] || 0) + (r.totalPurchaseValue || 0);
    });

    let topCategory = "N/A";
    let topCategoryValue = 0;
    Object.entries(categoryTotals).forEach(([cat, val]) => {
      if (val > topCategoryValue) {
        topCategoryValue = val;
        topCategory = cat;
      }
    });

    const uniqueProjectsCount = new Set(data.map((d) => d.project)).size;

    return {
      totalSpend,
      topCategory,
      topCategoryValue,
      uniqueProjectsCount,
    };
  }, [data]);

  const uniqueCategories = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.materialCategory).filter(Boolean)));
  }, [data]);

  const uniqueProjects = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.project).filter(Boolean)));
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Project / Site Name",
        field: "project",
        colId: "project",
        flex: 1.6,
        minWidth: 180,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "var(--color-primary, #0b6462)" }}>{p.value}</span>
        ),
      },
      {
        headerName: "Material Category",
        field: "materialCategory",
        colId: "materialCategory",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span
            style={{
              background: "#f1f5f9",
              color: "#1e293b",
              padding: "3px 8px",
              borderRadius: "4px",
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Total Purchase Value (PKR)",
        field: "totalPurchaseValue",
        colId: "totalPurchaseValue",
        flex: 1.6,
        minWidth: 180,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0f172a" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Share of Total Spend (%)",
        field: "shareOfSpend",
        colId: "shareOfSpend",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => {
          const val = p.value || 0;
          return (
            <div className="d-flex align-items-center" style={{ gap: "8px", width: "100%" }}>
              <div
                style={{
                  flexGrow: 1,
                  background: "#e2e8f0",
                  height: "7px",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.min(val, 100)}%`,
                    background: "var(--color-primary, #0b6462)",
                    height: "100%",
                  }}
                />
              </div>
              <span
                style={{ fontWeight: 700, color: "#334155", fontSize: "12px", minWidth: "40px" }}
              >
                {val}%
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Weighted Avg Price per Unit",
        field: "weightedAvgPrice",
        colId: "weightedAvgPrice",
        flex: 1.7,
        minWidth: 190,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#0284c7" }}>{p.value}</span>,
      },
      {
        headerName: "Monthly Trend vs Baseline",
        field: "monthlyTrend",
        colId: "monthlyTrend",
        flex: 1.4,
        minWidth: 160,
        cellRenderer: (p) => {
          const isUp = p.data?.trendDirection === "up";
          const isDown = p.data?.trendDirection === "down";
          const color = isUp ? "#dc2626" : isDown ? "#16a34a" : "#64748b";
          const icon = isUp ? "↑" : isDown ? "↓" : "—";
          return (
            <span
              style={{
                color,
                fontWeight: 700,
                background: isUp ? "#fef2f2" : isDown ? "#f0fdf4" : "#f1f5f9",
                padding: "2px 8px",
                borderRadius: "10px",
                fontSize: "12px",
              }}
            >
              {icon} {p.value}
            </span>
          );
        },
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.project && row.project.toLowerCase().includes(q)) ||
          (row.materialCategory && row.materialCategory.toLowerCase().includes(q)) ||
          (row.weightedAvgPrice && row.weightedAvgPrice.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (categoryFilter !== "all" && row.materialCategory !== categoryFilter) {
        return false;
      }

      if (projectFilter !== "all" && row.project !== projectFilter) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, categoryFilter, projectFilter]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Procurement_Spend_Material_Category_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`,
      });
      toast.success("Procurement spend analysis exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL PROCUREMENT SPEND"
            value={`PKR ${(kpis.totalSpend / 1000000).toFixed(2)}M`}
            subtitle={`PKR ${kpis.totalSpend.toLocaleString()} across projects`}
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOP SPEND CATEGORY"
            value={kpis.topCategory}
            subtitle={`PKR ${(kpis.topCategoryValue / 1000000).toFixed(1)}M spend share`}
            icon={FiLayers}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="PROJECT SITES COVERED"
            value={`${kpis.uniqueProjectsCount} Sites`}
            subtitle="Active construction capital allocation"
            icon={FiPieChart}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="PRICE STABILITY INDEX"
            value="+2.1% MoM"
            subtitle="Weighted avg inflation across core materials"
            icon={FiTrendingUp}
            color="#d97706"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Procurement Spend Analysis"
            >
              Procurement Spend Analysis
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search category, project..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Category Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {uniqueCategories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <label>Material Category</label>
              </div>
            </div>

            {/* Project Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                >
                  <option value="all">All Projects</option>
                  {uniqueProjects.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <label>Project / Site</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcurementSpendAnalysisReport;
